import * as Sentry from "@sentry/react";
import axios, { isAxiosError } from "axios";
import { useAuthStore } from "@store/auth/auth";
import { clearUserState } from "@utils/common";
import {
  API_URL,
  APP_VERSION,
  BUDGET_API_URL,
  CMS_API_URL,
  COUNTRY_CODE,
  CUSTOMER_BANK_CONSENT_LEDGER_API_URL,
  CUSTOMER_DEBT_API_URL,
  CUSTOMER_SCREENING_API_URL,
  CUSTOMER_STORAGE_API_URL,
  IS_APP_KYC_MODE,
  STATUS_API_URL,
  TRANSLATIONS_API_URL,
} from "@/constants";

const USER_AGENT =
  "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/130.0.0.0 Safari/537.36";

const createAxiosInstance = (baseURL: string, tokenIsBearer?: boolean) => {
  const instance = axios.create({ baseURL });

  instance.defaults.timeout = 10000;
  instance.defaults.headers.common = {
    "Content-Type": "application/json",
  };

  instance.interceptors.request.use(
    (config) => {
      const token = useAuthStore.getState().token;

      if (config?.authorized && !token) {
        clearUserState();

        return Promise.reject();
      }

      if (token) {
        if (tokenIsBearer) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          config.headers.Token = token;
        }
      }

      if (typeof window !== "undefined") {
        config.headers.Platform = window.navigator.userAgent;
      } else {
        config.headers.Platform = USER_AGENT;
      }
      config.headers.Version = APP_VERSION;
      config.headers.Source = IS_APP_KYC_MODE ? "a" : "w";
      config.headers.CountryCode = COUNTRY_CODE;

      return config;
    },
    (error) => {
      // Logout if unauthorized
      if (
        isAxiosError(error) &&
        error.response &&
        error.response.status === 401
      ) {
        clearUserState();
      }

      return Promise.reject(error);
    },
  );

  instance.interceptors.response.use(
    (response) => {
      // Internal redirect response code
      if (
        typeof window !== "undefined" &&
        response.status === 209 &&
        response.data
      ) {
        window.location.replace(response.data as string);

        // sleeping till redirect happens
        return new Promise((resolve) => setTimeout(resolve, 3000));
      }

      // No need to do anything, take rest
      return Promise.resolve(response);
    },
    (error) => {
      if (isAxiosError(error) && error.response) {
        // Logout if unauthorized
        if (error.response.status === 401) {
          clearUserState();
        }

        if (
          error.response?.status === 404 &&
          error?.config &&
          error.config?.url !== "/authentication/facebook" // if user has no FB connection it returns 404 - expected behavior
        ) {
          Sentry.captureException(
            `Request failed (404): ${error.config?.method} ${error.config?.url}`,
          );
        }
      }

      return Promise.reject(error);
    },
  );

  return instance;
};

export const assetsApi = createAxiosInstance("");
export const api = createAxiosInstance(API_URL);
export const apiV3 = createAxiosInstance(API_URL.replace("/v2", "/v3"));
// export const statusApi = createAxiosInstance(STATUS_API_URL);
export const statusApi = axios.create({ baseURL: STATUS_API_URL });
export const translationsApi = createAxiosInstance(TRANSLATIONS_API_URL);
export const budgetApi = createAxiosInstance(BUDGET_API_URL + "/v1", true);
export const budgetApiV2 = createAxiosInstance(BUDGET_API_URL + "/v2", true);
export const customerDebtApi = createAxiosInstance(CUSTOMER_DEBT_API_URL);
export const customerStorageApi = createAxiosInstance(
  CUSTOMER_STORAGE_API_URL,
  true,
);
export const knowYourCustomerApi = createAxiosInstance(
  CUSTOMER_SCREENING_API_URL,
  true,
);
export const customerBankConsentLedgerApi = createAxiosInstance(
  CUSTOMER_BANK_CONSENT_LEDGER_API_URL + "/v1",
  true,
);
export const cmsApi = axios.create({ baseURL: CMS_API_URL });
